import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {isNullOrUndefined} from "util";
import {GgwDeviceParam} from "@catmodules/models/ggw-device-params/types";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {ActiveModal} from "@catmodules/widgets/slide-in-modal/utils/modal-ref";
import {FeatureService, NotificationsService} from "@catcore/services";
import {StyleType} from "@catui/types";
import {ParametersService} from "@catmodules/models/parameters/services/parameters.service";
import {FeatureGgwDeviceParamService} from "@catmodules/models/ggw-device-params/services/feature-ggw-device-param.service";
import {extractDataFromModel, sortByLabelFunc} from "@catcore/utils";
import {ProjectGgwDeviceFormComponent} from "@catmodules/models/ggw-device/components/project-ggw-device-form/project-ggw-device-form.component";
import {ProjectGgwDeviceParamService} from "@catmodules/models/ggw-device-params/services/project-ggw-device-param.service";
import {SelectItem} from "primeng/primeng";
import {EntitySchemaType} from "@catcore/utils/json-schema/entity-schema-to-json-schema";
import {EntitySchemaService} from "@catcore/services/entity-schema.service";
import {FeatureOptionTokenService} from "@catmodels/feature-option-tokens/services/feature-option-token.service";
import {Subscription} from "rxjs";
import {FeatureOptionToken} from "@catmodels/feature-option-tokens/types";

@Component({
  selector: 'catapp-modify-ggw-device-param',
  templateUrl: './modify-ggw-device-param.component.html',
  styleUrls: ['./modify-ggw-device-param.component.scss']
})
export class ModifyGgwDeviceParamComponent implements OnInit, OnDestroy {

  private _parameter: GgwDeviceParam;
  private _ggwParamOptionsByType: any = {};

  private modelSchema;
  private optionTokens: any[];
  private optionTokenSubscription: Subscription;

  optionTokensOptions: SelectItem[] = null;

  parameterOptionValueField = 'value';
  parameterOptionLabel = 'label';
  ggwDeviceUri: string;

  includeExcludeOptions: SelectItem[] = [];

  controls = {
    key: 'key',
    value: 'value',
    type: 'parameter_type',
    includeExclude: 'include_exclude_for',
    token: 'option_token',
  };

  labels = {
    key: 'Key',
    value: 'Value',
    type: 'Type',
    includeExclude: 'Conditional',
    token: 'Option Token',
  };

  typeOptions = [
    {value: 'generic', label: 'Text'}
  ];

  form: FormGroup;
  styleType = StyleType;


  @Input() featureLevel = false;
  @Input() feature = null;

  // Emitted when the parameter type changes
  @Output() updateOptionsForParamType = new EventEmitter<string>();

  @Output() afterDelete = new EventEmitter();
  @Output() afterUpdate = new EventEmitter<GgwDeviceParam>();

  @Input()
  get parameter(): GgwDeviceParam {
    return this._parameter;
  }

  @Input() fileList: any[];

  @Input()
  set ggwParameterOptions(value) {
    if (value) {
      this._ggwParamOptionsByType = value;
      this.typeOptions = [
        {value: 'generic', label: 'Text'},
        ...this._ggwParamOptionsByType.type_options
      ];
    } else {
      this._ggwParamOptionsByType = {};
    }
  }

  get ggwParameterOptions() {
    return this._ggwParamOptionsByType;
  }

  getTypeLabel(typeValue) {
    let option = this.typeOptions.find((option) => option.value === typeValue);
    if (option) {
      return option.label;
    } else {
      return 'Text';
    }
  }

  set parameter(value: GgwDeviceParam) {
    this._parameter = value;

    if (!isNullOrUndefined(this._parameter)) {
      if (this.isLinkedParameter(this._parameter)) {
        this.updateOptionsForParamType.emit(this._parameter.parameter_type);
      }
      this.form.patchValue(this._parameter);
    }
  }

  get dataIsReady() {
    return !isNullOrUndefined(this._parameter);
  }

  constructor(private formBuilder: FormBuilder,
              private genericResourceService: GenericResourceService,
              private activeModal: ActiveModal,
              private notification: NotificationsService,
              private parametersService: ParametersService,
              private featureGgwDeviceParamService: FeatureGgwDeviceParamService,
              private projectGgwDeviceParamService: ProjectGgwDeviceParamService,
              schemaService: EntitySchemaService,
              private featureService: FeatureService) {
    this.modelSchema = schemaService.getForEntityName(EntitySchemaType['FeatureGgwDeviceParameters']);
    this.form = this.formBuilder.group({
      key: ['', [Validators.required]],
      value: [null, []],
      parameter_type: [this.typeOptions[0].value, []],
      include_exclude_for: [null, []],
      option_token: [null, []]
    });
  }

  ngOnInit() {
    if (this.modelSchema) {
      this.updateIncludeExcludeOptions(this.modelSchema.properties.include_exclude_for.choices);
    }
    if (this.featureLevel && this.feature) {
      this.optionTokenSubscription = this.featureService.getOptionTokens(this.feature)
        .finally(() => this.optionTokenSubscription.unsubscribe())
        .subscribe((tokens: FeatureOptionToken[]) => {
          this.optionTokens = tokens;
          this.updateTokenOptions(this.optionTokens);
        });
    }
    if (!isNullOrUndefined(this._parameter) && !isNullOrUndefined(this._parameter._links.option_token)) {
      if (this.isLinkedParameter(this._parameter)) {
        this.updateOptionsForParamType.emit(this._parameter.parameter_type);
      }
      this.form.patchValue({option_token: this._parameter._links.option_token.href});
    }
  }

  ngOnDestroy() {
    if (this.optionTokenSubscription) {
      this.optionTokenSubscription.unsubscribe();
    }
  }

  /**
   * Returns true if the parameter has a static option list, e.g. the options are attached to
   * the parameter model.
   *
   * @param parameter
   */
  isParameterWithStaticOptions(parameter): boolean {
    return parameter && this.parametersService.isParameterWithOptions(parameter);
  }

  /**
   * Returns true if the parameter is of linked entity type (e.g. ggw device, ggw impl, port).
   *
   * @param parameter
   */
  isLinkedParameter(parameter) {
    return parameter && this.isLinkedParamType(parameter.parameter_type);
  }

  isLinkedParamType(paramType): boolean {
    return this._ggwParamOptionsByType !== null && this._ggwParamOptionsByType.hasOwnProperty(paramType);
  }

  isFileParameter(parameter) {
    return parameter && this.parametersService.isFileParameter(parameter);
  }

  isParameterWithOptions(parameter): boolean {
    return this.isParameterWithStaticOptions(parameter)
      || this.isLinkedParameter(parameter);
  }

  hasModelOrFeature(parameter): boolean {
    return this.featureLevel && parameter._links.hasOwnProperty('model')
      || !this.featureLevel && parameter._links.hasOwnProperty('type');
  }

  staticParameterOptions(parameter): any[] {
    let paramOptions = [];
    if (this.parametersService.isParameterWithOptions(parameter)) {
      paramOptions = this.parametersService.parameterOptions(parameter);
    }
    paramOptions.sort(sortByLabelFunc);
    return paramOptions;
  }

  /**
   * Returns the allowed options for a linked parameter of the given type. If a category is
   * supplied as second parameter, the choice is limited to the matching category. Otherwise,
   * the values of all categories can be chosen.
   *
   * @param parameterType
   * @param categories
   */
  linkedParameterOptionsForType(parameterType, categories = null) {
    let paramOptions = [];

    if (parameterType && this.ggwParameterOptions.hasOwnProperty(parameterType)) {
      const optionsByCategory = this.ggwParameterOptions[parameterType];
      let allowedCategories = [];
      if (categories !== null && categories.length > 0) {
        allowedCategories = [
          'all',
          ...categories
        ];
      } else {
        allowedCategories = [
          ...Object.keys(optionsByCategory)
        ];
      }
      allowedCategories.forEach((category) => {
        if (optionsByCategory.hasOwnProperty(category)) {
          paramOptions = [
            ...paramOptions,
            ...optionsByCategory[category]
          ];
        }
      });
    }

    paramOptions.sort(sortByLabelFunc);
    return paramOptions;
  }

  /**
   * Saves the current parameter
   */
  saveParameter() {
    if (this._parameter) {
      const resourceService = this.genericResourceService.findServiceForResource(this._parameter._links.self.href);
      const formValue = this.form.value;
      console.log('formvalue: ', formValue);
      let item = {
        id: this._parameter.id,
        parameter_type: formValue.parameter_type
      };
      if (this.isLinkedParamType(formValue.parameter_type)) {
        const selectedOption = this.getSelectedOption(this._parameter, formValue.parameter_type);
        if (selectedOption) {
          item[selectedOption.attribute_name] = formValue.value;
        }
      } else {
        item['value'] = formValue.value;
      }
      // includeFor /excludeFor should only be modified on feature level
      if (this.featureLevel) {
        item['include_exclude_for'] = formValue.include_exclude_for;
        if (item['include_exclude_for'] !== null) {
          item['option_token'] = formValue.option_token;
        } else {
          item['option_token'] = null;
        }
      }
      console.warn('Item to save: ', item);
      resourceService.save(item).subscribe((result) => {
        this.notification.success('GGW device parameter updated', 'The ggw device parameter was successfully updated.');
        this.afterUpdate.emit(result as GgwDeviceParam);
        this.activeModal.close();
      });
    } else {
      // Create new
      let item = this.form.value;
      console.log('form value: ', item);
      item['ggw_device'] = this.ggwDeviceUri;
      if (this.isLinkedParamType(item.parameter_type)) {
        const selectedOption = this.getOptionForTypeAndValue(item.parameter_type, item.value);
        if (selectedOption) {
          item[selectedOption.attribute_name] = item.value;
          if (selectedOption.attribute_name !== 'value') {
            delete item.value;
          }
        } else {
          item['value'] = null;
        }
      }
      // includeFor /excludeFor should only be modified on feature level
      if (!this.featureLevel) {
        delete item['include_exclude_for'];
        delete item['option_token'];
      }
      let createService: any;
      if (this.featureLevel) {
        createService = this.featureGgwDeviceParamService;
      } else {
        createService = this.projectGgwDeviceParamService;
      }
      createService.create(item).subscribe((result) => {
        this.notification.success('GGW device parameter created', 'The ggw device parameter was successfully created.');
        this.afterUpdate.emit(result as GgwDeviceParam);
        this.activeModal.close();
      });
    }
  }

  /**
   * Deletes the current parameter
   */
  deleteParameter() {
    const confirmDelete = confirm('Are you sure you want to delete this parameter?');
    if (confirmDelete) {
      const ggwDeviceParamService = this.genericResourceService.findServiceForResource(this._parameter._links.self.href);
      ggwDeviceParamService
        .delete()
        .subscribe(() => {
          this.notification.success('GGW device parameter deleted', 'The parameter was successfully removed.');
          this.afterDelete.emit();
          this.activeModal.close();
        });
    }
  }

  private getSelectedOption(parameter, parameterType) {
    let options = this.staticParameterOptions(parameter);
    if (this.isLinkedParamType(parameterType)) {
      options = [
        ...options,
        ...this.linkedParameterOptionsForType(parameterType)
      ];
    }
    return options.find((option) => option.value === this.form.value.value);
  }

  private getOptionForTypeAndValue(paramType, value) {
    const options = this.linkedParameterOptionsForType(paramType);
    return options.find((option) => option.value === value);
  }

  private updateIncludeExcludeOptions(choices: any[]) {
    const items: SelectItem[] = [];
    items.push({label: 'No', value: null});
    choices.forEach((choice: any) => {
      if (choice) {
        items.push({label: choice, value: choice});
      }
    });
    this.includeExcludeOptions = items;
  }

  private updateTokenOptions(tokens: FeatureOptionToken[]) {
    const items: SelectItem[] = [];
    items.push({label: '', value: null});
    tokens.forEach((token: FeatureOptionToken) => {
      console.log(token);
      items.push({label: token.value, value: token._links.self.href});
    });
    this.optionTokensOptions = items;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/ggw-device-params/components/modify-ggw-device-param/modify-ggw-device-param.component.ts